import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';

const FONT_SANS = "'Geist', 'SF Pro Display', -apple-system, sans-serif";
const FONT_MONO = "'Geist Mono', 'SF Mono', monospace";
const API = '/api/chamas';

export default function ChamaChat({ chamaId, marketId, onBack }) {
  const { T } = useTheme();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const token = localStorage.getItem('token');
  const pollRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    // Poll every 8 seconds for new messages
    pollRef.current = setInterval(fetchMessages, 8000);
    return () => clearInterval(pollRef.current);
  }, [chamaId, marketId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async (prepend = false) => {
    try {
      const params = new URLSearchParams();
      if (marketId) params.set('market_id', marketId);
      params.set('limit', '50');
      const res = await fetch(`${API}/${chamaId}/messages?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      setMessages(d.messages || []);
    } catch (e) {
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/${chamaId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: text.trim(), market_id: marketId || undefined }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setMessages(prev => [...prev, d.message]);
      setText('');
    } catch (e) {
      setError(e.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const timeLabel = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12 };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: FONT_SANS, color: T.text, display: 'flex', flexDirection: 'column' }}>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {/* Nav */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 100, height: 56,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 24px',
        background: `${T.bg}ee`, backdropFilter: 'blur(16px)', borderBottom: `1px solid ${T.border}`,
        flexShrink: 0,
      }}>
        <button onClick={onBack} style={{
          fontSize: 13, color: T.textMuted, padding: '6px 14px', borderRadius: 8,
          border: `1px solid ${T.border}`, background: T.surface, cursor: 'pointer', fontFamily: FONT_SANS,
        }}>← Back</button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
            {marketId ? 'Market Discussion' : 'Chama Chat'}
          </div>
          {marketId && (
            <div style={{ fontSize: 11, color: T.textDim, fontFamily: FONT_MONO }}>Market #{marketId}</div>
          )}
        </div>
        <div style={{ width: 80 }} />
      </nav>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', maxWidth: 760, width: '100%', margin: '0 auto', boxSizing: 'border-box' }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: T.textDim, fontSize: 14, paddingTop: 60 }}>Loading messages...</div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: T.textDim, fontSize: 14, paddingTop: 60 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>💬</div>
            No messages yet. Start the discussion!
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, animation: 'fadeIn 0.3s ease' }}>
            {messages.map((m, i) => {
              const prevMsg = messages[i - 1];
              const showName = !prevMsg || prevMsg.user_id !== m.user_id ||
                (new Date(m.created_at) - new Date(prevMsg.created_at)) > 5 * 60 * 1000;
              const initials = (m.username || '??').slice(0, 2).toUpperCase();

              return (
                <div key={m.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: showName ? 12 : 2 }}>
                  {showName ? (
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                      background: T.accentDim, border: `1px solid ${T.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700, color: T.accent, fontFamily: FONT_MONO,
                      marginTop: 2,
                    }}>{initials}</div>
                  ) : (
                    <div style={{ width: 32, flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {showName && (
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{m.username}</span>
                        <span style={{ fontSize: 11, color: T.textDim, fontFamily: FONT_MONO }}>{timeLabel(m.created_at)}</span>
                      </div>
                    )}
                    <div style={{
                      background: T.surfaceHi, border: `1px solid ${T.border}`,
                      borderRadius: showName ? '4px 12px 12px 12px' : '4px 12px 12px 4px',
                      padding: '9px 13px', display: 'inline-block', maxWidth: '100%',
                    }}>
                      <p style={{ color: T.text, fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {m.content}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{
        padding: '12px 24px 20px', background: `${T.bg}f5`, backdropFilter: 'blur(12px)',
        borderTop: `1px solid ${T.border}`, flexShrink: 0,
      }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          {error && (
            <div style={{
              marginBottom: 8, padding: '8px 12px', borderRadius: 7, fontSize: 12,
              background: 'rgba(234,57,67,0.1)', border: '1px solid rgba(234,57,67,0.2)', color: '#ea3943',
            }}>{error}</div>
          )}
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Message the chama... (Enter to send, Shift+Enter for new line)"
              rows={1}
              maxLength={1000}
              style={{
                flex: 1, background: T.surfaceHi, border: `1px solid ${T.border}`,
                borderRadius: 10, padding: '11px 14px', color: T.text,
                fontFamily: FONT_SANS, fontSize: 14, outline: 'none',
                resize: 'none', lineHeight: 1.5, maxHeight: 120, overflowY: 'auto',
              }}
            />
            <button
              onClick={sendMessage}
              disabled={sending || !text.trim()}
              style={{
                padding: '11px 20px', borderRadius: 10, border: 'none',
                background: text.trim() ? T.accent : T.surfaceHi,
                color: text.trim() ? '#fff' : T.textDim,
                fontFamily: FONT_SANS, fontSize: 14, fontWeight: 600,
                cursor: text.trim() ? 'pointer' : 'not-allowed',
                opacity: sending ? 0.6 : 1, flexShrink: 0,
              }}
            >
              {sending ? '...' : 'Send'}
            </button>
          </div>
          <div style={{ color: T.textDim, fontSize: 11, marginTop: 6 }}>
            {text.length}/1000 · Refreshes every 8 seconds
          </div>
        </div>
      </div>
    </div>
  );
}